<script lang="ts" setup>
import { useSortable } from '@vueuse/integrations/useSortable';
import { storeToRefs } from 'pinia';
import { nextTick, toRefs, useTemplateRef } from 'vue';

import type { RouteError } from '@nora-monorepo/shared';

import RouteListItem from '#nora-ui/map/components/drawtools/route-tool/RouteListItem.vue';
import type { RouteTool } from '#nora-ui/map/drawtools/route-tool';

interface Props {
  tool: RouteTool;
  routeError: RouteError | null;
}

const props = defineProps<Props>();
const dragDropParentRef = useTemplateRef<HTMLElement>('dragDropParentRef');
const { tool, routeError } = toRefs(props);
const routeToolStore = tool.value.routeToolStore;
const { routeCoordinates } = storeToRefs(routeToolStore);

function deletePoint(index: number) {
  tool.value.deletePoint(index);
}

useSortable(dragDropParentRef, routeCoordinates, {
  handle: '.drag-handle',
  animation: 150, // animation duration in ms
  onEnd: async () => {
    // Clear errors since point indices have changed
    tool.value.routeToolStore.clearErrors();

    // We wait for the next tick to make sure routeCoordinates has been updated
    await nextTick();

    tool.value.routeToolStore.updateRouteCoordinates(
      routeCoordinates.value,
    );
    await tool.value.handleRouteChange();
    tool.value.routeToolStore.updatePointFeatures(
      tool.value.editingVectorSource,
    );
  },
});
</script>

<template>
  <ul
    ref="dragDropParentRef"
    class="w-full max-h-[30vh] p-2 overflow-y-auto shrink"
  >
    <li
      v-for="(coordinate, index) of routeCoordinates"
      :key="`${coordinate.latitude} ${coordinate.longitude} ${index}`"
      class="drag-handle"
    >
      <RouteListItem
        :coordinate="coordinate"
        :delete-point="deletePoint"
        :index="index"
        :is-error="index === routeError?.pointIndex"
      />
    </li>
  </ul>
</template>
